import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'
import { Check, Image as ImageIcon, RefreshCw, Upload } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import {
  emptyPopupForm,
  popupFormSchema,
  popupFormToFormData,
  popupFrequencyLabels,
  popupToFormValues,
  type PopupFormValues,
} from './popup-form'

import { FormError } from '@/app/components/ui/FormError'
import { Button } from '@/app/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import { Spinner } from '@/app/lib/loader/loader'
import {
  type PopupApiError,
  type PopupFrequency,
  popupService,
} from '@/app/services/popup.service'

interface PopupFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  popupId: number | null
  onSuccess: () => void
}

const serverFieldMap: Record<string, keyof PopupFormValues> = {
  name: 'name',
  image: 'image',
  call_to_action_url: 'call_to_action_url',
  sort_order: 'sort_order',
  is_active: 'is_active',
  starts_at: 'starts_at',
  ends_at: 'ends_at',
  show_frequency: 'show_frequency',
  show_again_after_days: 'show_again_after_days',
}

export function PopupFormModal({
  open,
  onOpenChange,
  popupId,
  onSuccess,
}: PopupFormModalProps) {
  const isEditing = Boolean(popupId)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [previewUrl, setPreviewUrl] = useState('')
  const objectUrl = useRef<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PopupFormValues>({
    resolver: zodResolver(popupFormSchema),
    defaultValues: emptyPopupForm,
  })

  const frequency = watch('show_frequency')
  const isActive = watch('is_active')

  const releaseObjectUrl = useCallback(() => {
    if (objectUrl.current) {
      URL.revokeObjectURL(objectUrl.current)
      objectUrl.current = null
    }
  }, [])

  useEffect(() => {
    if (!open) return

    releaseObjectUrl()
    if (!popupId) {
      reset(emptyPopupForm)
      setPreviewUrl('')
      setLoadError(false)
      setIsLoading(false)
      return
    }

    let active = true
    setIsLoading(true)
    setLoadError(false)

    void popupService
      .getPopup(popupId)
      .then((popup) => {
        if (!active) return
        reset(popupToFormValues(popup))
        setPreviewUrl(popup.image_url)
      })
      .catch(() => {
        if (active) setLoadError(true)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [open, popupId, releaseObjectUrl, reloadKey, reset])

  useEffect(() => () => releaseObjectUrl(), [releaseObjectUrl])

  const handleFile = (file: File | null) => {
    releaseObjectUrl()
    setValue('image', file, { shouldDirty: true, shouldValidate: true })

    if (file) {
      objectUrl.current = URL.createObjectURL(file)
      setPreviewUrl(objectUrl.current)
    } else {
      setPreviewUrl(watch('current_image_url'))
    }
  }

  const applyServerErrors = (apiError: PopupApiError) => {
    Object.entries(apiError.errors ?? {}).forEach(([field, messages]) => {
      const formField = serverFieldMap[field]
      const message = Array.isArray(messages) ? messages[0] : messages
      if (formField && message) {
        setError(formField, { type: 'server', message })
      }
    })
  }

  const submit = async (values: PopupFormValues) => {
    try {
      const data = popupFormToFormData(values)
      if (popupId) {
        await popupService.updatePopup(popupId, data)
        toast.success('Popup actualizado exitosamente')
      } else {
        await popupService.createPopup(data)
        toast.success('Popup creado exitosamente')
      }

      releaseObjectUrl()
      onSuccess()
    } catch (error) {
      const apiError = isAxiosError<PopupApiError>(error)
        ? error.response?.data
        : undefined
      if (apiError?.errors) applyServerErrors(apiError)
      toast.error(apiError?.message || 'No fue posible guardar el popup')
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isSubmitting) onOpenChange(false)
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <form onSubmit={handleSubmit(submit)} noValidate>
          <DialogHeader>
            <DialogTitle className="text-card-foreground flex items-center gap-2 text-lg font-semibold">
              <ImageIcon className="text-primary h-5 w-5" />
              {isEditing ? 'Editar Popup' : 'Nuevo Popup'}
            </DialogTitle>
            <DialogDescription className="text-card-description">
              {isEditing
                ? 'Modifica los datos y la imagen del popup.'
                : 'Configura un nuevo popup para la tienda en línea.'}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-8 w-8" />
            </div>
          ) : loadError ? (
            <div className="flex min-h-64 flex-col items-center justify-center text-center">
              <p className="font-medium">No fue posible cargar el popup</p>
              <Button
                type="button"
                variant="outline"
                className="mt-4"
                onClick={() => setReloadKey((value) => value + 1)}
              >
                <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
              </Button>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="popup-name" className="text-card-foreground text-sm font-medium">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="popup-name"
                    placeholder="Ej. Promoción de temporada"
                    {...register('name')}
                    aria-invalid={Boolean(errors.name)}
                  />
                  <FormError message={errors.name?.message} />
                </div>

                <div className="space-y-2">
                  <label htmlFor="popup-order" className="text-card-foreground text-sm font-medium">
                    Orden <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="popup-order"
                    type="number"
                    min="0"
                    step="1"
                    {...register('sort_order')}
                    aria-invalid={Boolean(errors.sort_order)}
                  />
                  <FormError message={errors.sort_order?.message} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="popup-start" className="text-card-foreground text-sm font-medium">
                    Fecha de Inicio (Opcional)
                  </label>
                  <Input
                    id="popup-start"
                    type="date"
                    {...register('starts_at')}
                    aria-invalid={Boolean(errors.starts_at)}
                  />
                  <FormError message={errors.starts_at?.message} />
                </div>

                <div className="space-y-2">
                  <label htmlFor="popup-end" className="text-card-foreground text-sm font-medium">
                    Fecha de Fin (Opcional)
                  </label>
                  <Input
                    id="popup-end"
                    type="date"
                    {...register('ends_at')}
                    aria-invalid={Boolean(errors.ends_at)}
                  />
                  <FormError message={errors.ends_at?.message} />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  id="popup-active"
                  type="button"
                  role="switch"
                  aria-checked={isActive}
                  onClick={() => setValue('is_active', !isActive, { shouldDirty: true })}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActive ? 'bg-primary' : 'bg-slate-200'
                    }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActive ? 'translate-x-2' : '-translate-x-2'
                      }`}
                  />
                </button>
                <label htmlFor="popup-active" className="text-card-foreground text-sm font-medium">
                  Popup Activo
                </label>
              </div>

              <hr />

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium">Imagen del popup</h3>
                  <p className="text-sm text-slate-500">
                    Carga una imagen JPG, PNG o WebP de máximo 5 MB.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 rounded-md border bg-white p-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-xs font-medium">
                      Imagen <span className="text-red-500">*</span>
                    </label>
                    {previewUrl ? (
                      <div className="group relative flex h-40 items-center justify-center rounded-md border bg-slate-50 p-1">
                        <img
                          src={previewUrl}
                          alt="Vista previa del popup"
                          className="max-h-full max-w-full object-contain"
                        />
                        <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                          <label className="cursor-pointer rounded-full bg-white p-2 text-slate-800" title="Reemplazar imagen">
                            <Upload size={16} />
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="hidden"
                              aria-label="Imagen *"
                              onChange={(event) => handleFile(event.target.files?.[0] ?? null)}
                            />
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className="flex h-40 cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed hover:bg-slate-50">
                        <Upload className="mb-2 text-slate-400" size={24} />
                        <span className="text-xs text-slate-500">Subir imagen</span>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          aria-label="Imagen *"
                          onChange={(event) => handleFile(event.target.files?.[0] ?? null)}
                        />
                      </label>
                    )}
                    <FormError message={errors.image?.message} />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="popup-cta" className="text-xs font-medium">
                      URL del CTA (Opcional)
                    </label>
                    <Input
                      id="popup-cta"
                      placeholder="https://..."
                      {...register('call_to_action_url')}
                      aria-invalid={Boolean(errors.call_to_action_url)}
                    />
                    <FormError message={errors.call_to_action_url?.message} />
                  </div>
                </div>
              </div>

              <hr />

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium">Frecuencia de visualización</h3>
                  <p className="text-sm text-slate-500">
                    Define cada cuánto puede volver a mostrarse al usuario.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor="popup-frequency" className="text-card-foreground text-sm font-medium">
                      Frecuencia <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={frequency}
                      onValueChange={(value) => setValue(
                        'show_frequency',
                        value as PopupFrequency,
                        { shouldDirty: true, shouldValidate: true },
                      )}
                    >
                      <SelectTrigger id="popup-frequency" aria-label="Frecuencia">
                        <SelectValue placeholder="Selecciona la frecuencia" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(popupFrequencyLabels).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormError message={errors.show_frequency?.message} />
                  </div>

                  {frequency === 'EVERY_N_DAYS' && (
                    <div className="space-y-2">
                      <label htmlFor="popup-days" className="text-card-foreground text-sm font-medium">
                        Volver a mostrar después de días <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="popup-days"
                        type="number"
                        min="1"
                        step="1"
                        {...register('show_again_after_days')}
                        aria-invalid={Boolean(errors.show_again_after_days)}
                      />
                      <FormError message={errors.show_again_after_days?.message} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4 gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isLoading || loadError}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
